import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { Compound } from '../../services/restserver-openapi';
import Container from '../../components/Container';
import Main from '../../layouts/Main';
import { useAppSelector } from '../../hooks/hooks';
import { AgentCuration, getCurations } from '../../utils/Agent/curations';
import {
    AgentProduct,
    createProduct,
    createEmptyCuratedSelectionProduct,
    deleteProduct,
    ProductWeekendOffset,
    getAgentProducts,
    updateProduct,
} from '../../utils/Agent/products';
import { compressImageFileToBase64 } from '../../utils/imageCompression';

const getCompoundTypeOptions = (): string[] => {
    return Object.keys(Compound.compoundType)
        .filter((compoundType) => Number.isNaN(Number(compoundType)));
};

const AgentProductsOverviewPage = (): JSX.Element => {
    const theme = useTheme();
    const mode = theme.palette.mode;
    const isVerifiedAgent = useAppSelector((state) => state.user.isVerifiedAgent);

    const [products, setProducts] = useState<AgentProduct[]>([]);
    const [curations, setCurations] = useState<AgentCuration[]>([]);
    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(true);

    const compoundTypeOptions = useMemo(() => getCompoundTypeOptions(), []);

    const selectedProduct = useMemo(() => {
        return products.find((product) => product.productId === selectedProductId) ?? createEmptyCuratedSelectionProduct();
    }, [products, selectedProductId]);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const [loadedCurations, storedProducts] = await Promise.all([
                    getCurations(true),
                    getAgentProducts(),
                ]);
                setCurations(loadedCurations);
                setProducts(storedProducts);
                setSelectedProductId(storedProducts[0]?.productId ?? null);
            } catch (loadError) {
                setError(loadError instanceof Error ? loadError.message : 'Produkte oder Curations konnten nicht geladen werden.');
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    const updateSelectedProduct = (nextProduct: AgentProduct) => {
        setProducts((prevState) => prevState.map((product) => (
            product.productId === nextProduct.productId ? nextProduct : product
        )));
    };

    const handleCreate = () => {
        const newProduct = {
            ...createEmptyCuratedSelectionProduct(),
            productId: -Date.now(),
        };
        setProducts((prevState) => [newProduct, ...prevState]);
        setSelectedProductId(newProduct.productId);
        setSuccessMessage('Neues Produkt erstellt.');
        setError('');
    };

    const handleDelete = async () => {
        if (!selectedProductId) {
            return;
        }

        try {
            if (selectedProductId > 0) {
                await deleteProduct(selectedProductId);
            }

            const nextProducts = products.filter((product) => product.productId !== selectedProductId);
            setProducts(nextProducts);
            setSelectedProductId(nextProducts[0]?.productId ?? null);
            setSuccessMessage('Produkt gelöscht.');
            setError('');
        } catch (deleteError) {
            setError(deleteError instanceof Error ? deleteError.message : 'Das Produkt konnte nicht gelöscht werden.');
        }
    };

    const handleSave = async () => {
        if (!selectedProduct.name.trim()) {
            setError('Bitte gib einen Produktnamen ein.');
            return;
        }

        if (selectedProduct.curationId === 0) {
            setError('Bitte wähle eine bestehende Curation aus.');
            return;
        }

        try {
            const savedProduct = selectedProduct.productId > 0
                ? await updateProduct(selectedProduct)
                : await createProduct({ ...selectedProduct, productId: 0 });

            const productWithLocalImages = {
                ...savedProduct,
                image: savedProduct.image ?? selectedProduct.image ?? null,
                curatorImage: savedProduct.curatorImage ?? selectedProduct.curatorImage ?? null,
            };

            setProducts((prevState) => {
                const existingIndex = prevState.findIndex((product) => product.productId === selectedProduct.productId);
                if (existingIndex === -1) {
                    return [productWithLocalImages, ...prevState];
                }

                const nextState = [...prevState];
                nextState[existingIndex] = productWithLocalImages;
                return nextState;
            });

            setSelectedProductId(productWithLocalImages.productId);
            setSuccessMessage('Produkt gespeichert.');
            setError('');
        } catch (saveError) {
            try {
                const refreshedProducts = await getAgentProducts();
                setProducts(refreshedProducts);

                const reconciledProduct = selectedProduct.productId > 0
                    ? refreshedProducts.find((product) => product.productId === selectedProduct.productId)
                    : refreshedProducts
                        .filter((product) => product.name === selectedProduct.name && product.curationId === selectedProduct.curationId)
                        .sort((a, b) => b.productId - a.productId)[0];

                if (reconciledProduct) {
                    setSelectedProductId(reconciledProduct.productId);
                    setSuccessMessage('Produkt gespeichert.');
                    setError('');
                    return;
                }
            } catch {
                // fall through to original error
            }

            setError(saveError instanceof Error ? saveError.message : 'Das Produkt konnte nicht gespeichert werden.');
        }
    };

    const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        try {
            const base64Image = await compressImageFileToBase64(file, {
                maxWidth: 1600,
                maxHeight: 1200,
                maxBytes: 1_000_000,
            });
            updateSelectedProduct({ ...selectedProduct, image: base64Image });
        } catch {
            setError('Das Produktbild konnte nicht verarbeitet werden.');
        }
    };

    const ActionRow = (props: {
        icon: React.ReactNode;
        title: string;
        subtitle?: string;
        active?: boolean;
        onClick?: () => void;
        color?: 'inherit' | 'error';
    }) => {
        const { icon, title, subtitle, active, onClick, color = 'inherit' } = props;

        return (
            <ListItem disablePadding>
                <ListItemButton
                    onClick={onClick}
                    selected={active}
                    sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        gap: 0.5,
                        '&.Mui-selected': {
                            bgcolor: 'action.selected',
                            '&:hover': { bgcolor: 'action.selected' },
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: color === 'error' ? 'error.main' : active ? 'primary.main' : 'text.secondary' }}>
                        {icon}
                    </ListItemIcon>

                    <ListItemText
                        primary={<Typography sx={{ fontWeight: 800 }} noWrap>{title}</Typography>}
                        secondary={subtitle}
                        secondaryTypographyProps={{ color: 'text.secondary' }}
                    />
                </ListItemButton>
            </ListItem>
        );
    };

    if (!isVerifiedAgent) {
        return (
            <Main>
                <Container>
                    <Paper elevation={0} sx={{ borderRadius: 4, p: 2, border: 0, boxShadow: 'none', bgcolor: 'background.paper' }}>
                        <Alert severity="error">
                            Sie haben keine Agent-Berechtigung. Womöglich ist Ihr Account noch nicht freigeschaltet.
                        </Alert>
                    </Paper>
                </Container>
            </Main>
        );
    }

    return (
        <Main>
            <Container>
                <Typography sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: 1 }} gutterBottom color="text.secondary">
                    Administration
                </Typography>

                <Box mb={3}>
                    <Typography variant="h3" component="h2" color="text.primary" fontWeight={700} lineHeight={1.2}>
                        Suchmasken
                        <Typography
                            component="span"
                            variant="inherit"
                            color="text.primary"
                            sx={{
                                ml: 1,
                                background: `linear-gradient(180deg, transparent 82%, ${alpha(
                                    mode === 'light' ? theme.palette.secondary.main : theme.palette.primary.main,
                                    0.3,
                                )} 0%)`,
                            }}
                        >
                            für Reisen
                        </Typography>
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Verwalte die Inhalte für die Karten auf der Travel-Planning-Seite.
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
                    <Paper elevation={0} sx={{ borderRadius: 4, p: 2, boxShadow: 'none', bgcolor: 'background.paper' }}>
                        <Typography variant="caption" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 1 }} color="text.secondary">
                            Actions
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />

                        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                            <ActionRow
                                icon={<AddCircleOutlineRoundedIcon />}
                                title="Neues Produkt"
                                subtitle="Kuratierte Auswahl anlegen"
                                onClick={handleCreate}
                            />
                            <ActionRow
                                icon={<DeleteOutlineRoundedIcon />}
                                title="Produkt löschen"
                                subtitle="Aktives Produkt entfernen"
                                onClick={handleDelete}
                                color="error"
                            />
                        </List>

                        <Typography variant="caption" fontWeight={900} sx={{ textTransform: 'uppercase', letterSpacing: 1 }} color="text.secondary">
                            Produkte
                        </Typography>
                        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
                            {products.map((product) => (
                                <ActionRow
                                    key={product.productId}
                                    icon={<Inventory2OutlinedIcon />}
                                    title={product.name || 'Unbenanntes Produkt'}
                                    subtitle={product.curationId ? `Curation #${product.curationId}` : 'Ohne Curation'}
                                    active={product.productId === selectedProductId}
                                    onClick={() => setSelectedProductId(product.productId)}
                                />
                            ))}
                            {!loading && products.length === 0 && (
                                <Typography color="text.secondary">Noch keine Produkte vorhanden.</Typography>
                            )}
                        </List>
                    </Paper>

                    <Paper elevation={0} sx={{ borderRadius: 4, p: 2, boxShadow: 'none', bgcolor: 'background.paper' }}>
                        <Stack spacing={2}>
                            <Typography variant="h6" fontWeight={700}>Editor</Typography>

                            <TextField
                                label="Produktname"
                                value={selectedProduct.name}
                                onChange={(event) => updateSelectedProduct({ ...selectedProduct, name: event.target.value })}
                                fullWidth
                            />

                            <TextField
                                label="Beschreibung"
                                value={selectedProduct.description}
                                onChange={(event) => updateSelectedProduct({ ...selectedProduct, description: event.target.value })}
                                fullWidth
                                multiline
                                minRows={3}
                            />

                            <FormControl fullWidth>
                                <InputLabel id="product-curation-label">Curation</InputLabel>
                                <Select
                                    labelId="product-curation-label"
                                    value={selectedProduct.curationId}
                                    label="Curation"
                                    onChange={(event) => updateSelectedProduct({ ...selectedProduct, curationId: Number(event.target.value) })}
                                >
                                    <MenuItem value={0}>Bitte wählen</MenuItem>
                                    {curations.map((curation) => (
                                        <MenuItem key={curation.curationId} value={curation.curationId}>
                                            {curation.curationName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel id="product-weekend-offset-label">Datumsmodus</InputLabel>
                                <Select
                                    labelId="product-weekend-offset-label"
                                    value={selectedProduct.weekendOffset}
                                    label="Datumsmodus"
                                    onChange={(event) => updateSelectedProduct({
                                        ...selectedProduct,
                                        weekendOffset: Number(event.target.value) as ProductWeekendOffset,
                                    })}
                                >
                                    <MenuItem value={ProductWeekendOffset.FLEXIBLE}>Freie Datumswahl</MenuItem>
                                    <MenuItem value={ProductWeekendOffset.NEXT_WEEKEND}>Nächstes Wochenende</MenuItem>
                                    <MenuItem value={ProductWeekendOffset.WEEKEND_AFTER_NEXT}>Übernächstes Wochenende</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel id="product-compound-types-label">Compoundtypen</InputLabel>
                                <Select
                                    multiple
                                    labelId="product-compound-types-label"
                                    value={selectedProduct.compoundTypes}
                                    onChange={(event) => {
                                        const values = event.target.value;
                                        updateSelectedProduct({
                                            ...selectedProduct,
                                            compoundTypes: typeof values === 'string' ? values.split(',') : values,
                                        });
                                    }}
                                    input={<OutlinedInput label="Compoundtypen" />}
                                    renderValue={(selected) => (
                                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                            {(selected as string[]).map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))}
                                        </Stack>
                                    )}
                                >
                                    {compoundTypeOptions.map((compoundType) => (
                                        <MenuItem key={compoundType} value={compoundType}>
                                            <Checkbox checked={selectedProduct.compoundTypes.includes(compoundType)} />
                                            <ListItemText primary={compoundType} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                <Avatar
                                    src={selectedProduct.image ? `data:image/*;base64,${selectedProduct.image}` : undefined}
                                    variant="rounded"
                                    sx={{ width: 96, height: 96 }}
                                >
                                    <CategoryRoundedIcon />
                                </Avatar>

                                <Stack spacing={1}>
                                    <Button variant="outlined" component="label">
                                        Produktbild hochladen
                                        <input hidden type="file" accept="image/*" onChange={handleImageChange} />
                                    </Button>
                                    <Button
                                        variant="text"
                                        color="inherit"
                                        onClick={() => updateSelectedProduct({ ...selectedProduct, image: null })}
                                    >
                                        Bild entfernen
                                    </Button>
                                </Stack>
                            </Stack>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectedProduct.showCurator}
                                        onChange={(event) => updateSelectedProduct({ ...selectedProduct, showCurator: event.target.checked })}
                                    />
                                }
                                label="Kurator anzeigen"
                            />

                            <Stack direction="row" spacing={1}>
                                <Button variant="contained" onClick={handleSave}>Speichern</Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Box>
            </Container>
        </Main>
    );
};

export default AgentProductsOverviewPage;
